/**
 * 羽归阁博客系统 - API客户端
 * 基于Parse SDK的统一API客户端
 */

import Parse from '@/lib/parse.ts';
import { ApiError } from '@/types/api';

const isProd = import.meta.env.PROD;
const runtimeOrigin = typeof window !== 'undefined' ? window.location.origin : '';
const resolvedParseServerURL =
  import.meta.env.VITE_PARSE_SERVER_URL ||
  (isProd
    ? import.meta.env.VITE_PROD_PARSE_SERVER_URL || `${runtimeOrigin}/parse`
    : import.meta.env.VITE_DEV_PARSE_SERVER_URL || 'http://localhost:1337/parse');

// 使用 import.meta.env 而不是 process.env (Vite 环境)
const PARSE_CONFIG = {
  applicationId: import.meta.env.VITE_PARSE_APP_ID || 'yuguige-blog-app-id',
  serverURL: resolvedParseServerURL,
  javascriptKey: import.meta.env.VITE_PARSE_JS_KEY || 'yuguige-blog-js-key',
  liveQueryServerURL:
    import.meta.env.VITE_PARSE_LIVE_QUERY_URL ||
    (isProd
      ? (runtimeOrigin || 'http://localhost').replace(/^http/i, 'ws')
      : 'ws://localhost:1337'),
};

export class ApiClient {
  private static instance: ApiClient;
  private initialized = false;

  private constructor() {}

  public static getInstance(): ApiClient {
    if (!ApiClient.instance) {
      ApiClient.instance = new ApiClient();
    }
    return ApiClient.instance;
  }

  /**
   * 初始化Parse SDK
   */
  public async init(): Promise<void> {
    try {
      Parse.initialize(PARSE_CONFIG.applicationId, PARSE_CONFIG.javascriptKey);
      Parse.serverURL = PARSE_CONFIG.serverURL;
      
      if (PARSE_CONFIG.liveQueryServerURL) {
        Parse.liveQueryServerURL = PARSE_CONFIG.liveQueryServerURL;
      }

      this.initialized = true;
      console.log('Parse SDK initialized successfully');
    } catch (error) {
      console.error('Failed to initialize Parse SDK:', error);
      throw error;
    }
  }

  /**
   * 检查是否已初始化
   */
  public isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * 获取当前用户
   */
  public getCurrentUser(): ReturnType<typeof Parse.User.current> {
    return Parse.User.current();
  }

  /**
   * 处理Parse错误
   */
  private handleParseError(error: { code: number; message: string }): ApiError {
    return {
      code: error.code.toString(),
      message: error.message,
      details: { originalError: error }
    };
  }
}

// 导出实例
export const apiClient = ApiClient.getInstance();

// Parse查询助手函数
export async function parseQuery(className: string, options: any = {}) {
  // 确保 Parse SDK 已初始化
  if (!apiClient.isInitialized()) {
    await apiClient.init();
  }
  
  const query = new Parse.Query(className);
  
  if (options.where) {
    Object.keys(options.where).forEach(key => {
      const value = options.where[key];
      
      // 处理 Pointer 类型的 where 条件
      if (value && typeof value === 'object' && value.__type === 'Pointer') {
        const PointerClass = Parse.Object.extend(value.className);
        const pointer = PointerClass.createWithoutData(value.objectId);
        query.equalTo(key, pointer);
      } else {
        query.equalTo(key, value);
      }
    });
  }
  
  if (options.include) {
    const includes = Array.isArray(options.include) ? options.include : [options.include];
    includes.forEach((include: string) => {
      query.include(include);
    });
  }
  
  if (options.limit) {
    query.limit(options.limit);
  }
  
  if (options.skip) {
    query.skip(options.skip);
  }
  
  if (options.orderBy) {
    query.ascending(options.orderBy);
  }
  
  if (options.orderByDescending) {
    query.descending(options.orderByDescending);
  }
  
  try {
    const results = await query.find();
    return results;
  } catch (error: any) {
    console.error(`❌ 查询 ${className} 失败:`, error);
    throw error;
  }
}

export async function parseGet(className: string, id: string, options: any = {}) {
  const query = new Parse.Query(className);
  
  if (options.include) {
    query.include(options.include);
  }
  
  return await query.get(id);
}

export async function parseCreate(className: string, data: any) {
  // 确保 Parse SDK 已初始化
  if (!apiClient.isInitialized()) {
    await apiClient.init();
  }
  
  const ParseObject = Parse.Object.extend(className);
  const object = new ParseObject();
  
  Object.keys(data).forEach(key => {
    const value = data[key];
    
    // 处理 Pointer 类型
    if (value && typeof value === 'object' && value.__type === 'Pointer') {
      const PointerClass = Parse.Object.extend(value.className);
      const pointer = PointerClass.createWithoutData(value.objectId);
      object.set(key, pointer);
    } else if (key === 'parentId' && value) {
      // 处理 parentId（也是 Pointer 类型）
      const ParentClass = Parse.Object.extend(className);
      const parent = ParentClass.createWithoutData(value);
      object.set(key, parent);
    } else {
      object.set(key, value);
    }
  });
  
  try {
    console.log(`📤 创建 ${className}，数据:`, data);
    const saved = await object.save();
    console.log(`✅ 成功创建 ${className}:`, saved.id, saved);
    return saved;
  } catch (error: any) {
    console.error(`❌ 创建 ${className} 失败:`, error);
    console.error('错误详情:', error.message, error.code, error);
    throw error;
  }
}

export async function parseUpdate(className: string, id: string, data: any) {
  const query = new Parse.Query(className);
  const object = await query.get(id);
  
  Object.keys(data).forEach(key => {
    object.set(key, data[key]);
  });
  
  return await object.save();
}

export async function parseDelete(className: string, id: string) {
  const query = new Parse.Query(className);
  const object = await query.get(id);
  return await object.destroy();
}

export async function parseCloudFunction(name: string, params: any = {}) {
  if (!apiClient.isInitialized()) {
    await apiClient.init();
  }
  console.log(name, params);
  
  return await Parse.Cloud.run(name, params);
}

export default apiClient;
